import posthog from 'posthog-js';
import { logger } from './logger';

const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.posthog.com'; // GDPR default

let isInitialized = false;

export const telemetry = {
  /** @internal Exposed for testing only */
  _isInitialized: false,

  /**
   * Inicializa la telemetría si las claves están configuradas
   */
  init: () => {
    const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
    if (!POSTHOG_KEY) {
      logger.info('Telemetry (PostHog) disabled: VITE_POSTHOG_KEY not set.');
      return;
    }

    try {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        autocapture: false, // Desactivar auto-captura para control estricto y GDPR
        capture_pageview: true,
        persistence: 'localStorage',
        mask_all_text: true, // Protección de privacidad
        mask_all_element_attributes: true,
        loaded: (ph) => {
          isInitialized = true;
          telemetry._isInitialized = true;
          logger.info('Telemetry (PostHog) initialized successfully.', { host: POSTHOG_HOST });
        }
      });
    } catch (error) {
      logger.error('Failed to initialize PostHog telemetry', error);
    }
  },

  /**
   * Identifica la sesión con el ID del técnico / administrador
   */
  identify: (userId: string, email: string, role: string) => {
    if (!isInitialized && !telemetry._isInitialized) return;
    try {
      posthog.identify(userId, {
        email,
        role,
        is_technician: role === 'technician',
        is_admin: role === 'admin'
      });
    } catch (e) {
      logger.error('Telemetry identify failed', e);
    }
  },

  /**
   * Limpia el identificador en logout
   */
  reset: () => {
    if (!isInitialized && !telemetry._isInitialized) return;
    try {
      posthog.reset();
    } catch (e) {
      logger.error('Telemetry reset failed', e);
    }
  },

  /**
   * Envía un evento genérico
   */
  track: (eventName: string, properties?: Record<string, any>) => {
    // Loguear localmente también en breadcrumbs
    logger.debug(`[Telemetry] Event: ${eventName}`, properties);

    if (!isInitialized && !telemetry._isInitialized) return;
    try {
      posthog.capture(eventName, {
        timestamp: new Date().toISOString(),
        ...properties
      });
    } catch (e) {
      logger.error(`Telemetry capture failed for event ${eventName}`, e);
    }
  },

  // ────────────────────────────────────────────────────────
  // Métricas Específicas del Flujo de Trabajo
  // ────────────────────────────────────────────────────────

  /**
   * Mide cuándo se abre un parte (punto de partida del funnel)
   */
  trackOrderOpen: (orderId: string, visibleId: string) => {
    telemetry.track('order_viewed', { orderId, visibleId });
  },

  /**
   * Inicio del proceso de validación y firma (funnel paso 1)
   */
  trackSignatureStart: (orderId: string, visibleId: string) => {
    telemetry.track('signature_flow_started', { orderId, visibleId });
  },

  /**
   * Firma completada y confirmada (funnel paso 2 - éxito)
   * @param durationSeconds Tiempo transcurrido en segundos desde la apertura del parte
   */
  trackSignatureComplete: (orderId: string, visibleId: string, totalDurationSeconds: number, modalDurationSeconds?: number) => {
    telemetry.track('signature_flow_completed', {
      orderId,
      visibleId,
      total_duration_seconds: totalDurationSeconds,
      modal_duration_seconds: modalDurationSeconds ?? 0
    });
  },

  /**
   * Abandono del modal de firma
   */
  trackSignatureAbort: (orderId: string, visibleId: string, reason: 'cancel' | 'clear') => {
    telemetry.track('signature_flow_aborted', { orderId, visibleId, reason });
  },

  /**
   * Archivar parte de trabajo
   */
  trackOrderArchive: (orderId: string, visibleId: string) => {
    telemetry.track('order_archived', { orderId, visibleId });
  },

  /**
   * Recuperar parte de trabajo del archivo
   */
  trackOrderRecover: (orderId: string, visibleId: string) => {
    telemetry.track('order_recovered', { orderId, visibleId });
  },

  /**
   * Eliminar parte de trabajo del historial
   */
  trackOrderDelete: (orderId: string, visibleId: string) => {
    telemetry.track('order_deleted', { orderId, visibleId });
  }
};
